import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { AppInsightsBaseService } from '@kros-sk/core/application-insights';
import { getCookie } from '@kros-sk/shared/utils-fn/cookie-utils';

import { NativeAppType } from '../types/device-types';
import { deviceDetectorActions } from '../data-access/store/device-detector.actions';
import { fromDeviceDetector } from '../data-access/store/device-detector.selectors';

/**
 * Service for communication with native app.
 *
 * This service is used to detect the type of native app and to trigger actions on the native app.
 */
@Injectable({
  providedIn: 'root',
})
export class NativeAppBridgeService {
  readonly #store = inject(Store);
  readonly #appInsights = inject(AppInsightsBaseService);

  readonly nativeAppType = this.#store.selectSignal(fromDeviceDetector.selectNativeAppType);

  #hasBeenDetected = false;

  /**
   * Message port for communication with native Chrome Custom Tabs app.
   */
  #messagePort: MessagePort | null = null;

  /**
   * Detects the type of native app and dispatches the result to the Device Detector store.
   * This can be an async process, so it is not possible to use this.nativeAppType() immediately after calling this method.
   */
  detectNativeAppType(): void {
    this.#hasBeenDetected = true;
    const hasIOsCookie = getCookie('ios-platform');

    if (hasIOsCookie) {
      this.#dispatchNativeAppResult(NativeAppType.IOS);
      // we are done here, we do not need to listen for messages for iOS
      return;
    }

    // we are listening for possible messages from native Chrome Custom Tabs
    this.#listenForMessages();
  }

  /**
   * Asking native app for user rating
   */
  rateApp(): void {
    if (!this.#hasBeenDetected) {
      console.error('NativeAppBridgeService.rateApp(): not detected yet');
      this.#appInsights.trackException(new Error('Not detected yet'), 'NativeAppBridgeService', { message: 'Not detected yet' });
      return;
    }

    if (this.nativeAppType() === NativeAppType.ANDROID) {
      this.#rateAndroidApp();
    } else if (this.nativeAppType() === NativeAppType.IOS) {
      this.#rateIosApp();
    }
  }

  /**
   * Send message to android app to rate the app.
   */
  #rateAndroidApp(): void {
    if (this.#messagePort) {
      this.#messagePort.postMessage('rate-app');
    } else {
      console.error('NativeAppBridgeService.rateAndroidApp(): no message port');
      this.#appInsights.trackException(new Error('No message port'), 'NativeAppBridgeService', { message: 'No message port' });
    }
  }

  /**
   * Send message to iOS web view to rate the app
   */
  #rateIosApp(): void {
    try {
      (window as any).webkit?.messageHandlers?.nativeApp?.postMessage('prompt-for-rating');
    } catch (error) {
      console.error('NativeAppBridgeService.rateIosApp(): error sending message to iOS web view', error);
      this.#appInsights.trackException(error as Error, 'NativeAppBridgeService', { message: 'Error sending message to iOS web view' });
    }
  }

  #dispatchNativeAppResult(nativeAppType: NativeAppType): void {
    this.#appInsights.trackEvent('SetNativeAppType', { nativeApp: nativeAppType });
    this.#store.dispatch(deviceDetectorActions.setNativeAppType({ nativeAppType }));
  }

  #listenForMessages(): void {
    window.addEventListener('message', (event): void => {
      const port = event.ports[0];
      if (typeof port === 'undefined') return;

      this.#messagePort = port;

      port.onmessage = (event): void => {
        this.#parseAndProcessMessage(event);
      };
    });
  }

  #parseAndProcessMessage(event: MessageEvent): void {
    const data = event.data;
    if (typeof data === 'string' && data.length > 0) {
      try {
        const message = JSON.parse(data);
        if (message?.isNativeApp) {
          // this is definitely an android app
          this.#dispatchNativeAppResult(NativeAppType.ANDROID);
        }
      } catch (error) {
        console.error('NativeAppBridgeService.parseAndProcessMessage(): error parsing message', error);
        this.#appInsights.trackException(error as Error, 'NativeAppBridgeService', { message: 'Error parsing message' });
      }
    }
  }
}
